/**
 * Segment Import Wizard
 *
 * Multi-step wizard for importing contacts from CSV into a new segment.
 * Steps:
 * 1. Upload CSV file
 * 2. Map CSV columns to contact fields
 * 3. Preview import data
 * 4. Execute import and view results
 */

import React, { useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../ui/Toast";
import {
  parseCsvFile,
  autoDetectMappings,
  previewImport,
  executeImport,
  appendToSegment,
  CONTACT_FIELD_OPTIONS,
  type FieldMapping,
  type ParsedCsvResult,
  type ImportPreviewRow,
  type ImportResult,
  type ContactFieldKey,
} from "../../services/segmentImport.service";
import {
  beginImportRun,
  stageAllChunks,
  previewImportRun,
  commitImportRun,
  abortImportRun,
  prepareAllImportContacts,
  mapRowsForImport,
  isLargeRemoval,
  classifyImportError,
  exceedsCommitBudget,
  REPLACE_IMPORT_MAX_ROWS,
  type PreviewResult as ReplacePreviewResult,
  type CommitResult as ReplaceCommitResult,
} from "../../services/segmentReplaceImport.service";

interface SegmentImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (segmentId: string) => void;
  mode?: "create" | "append";
  targetSegment?: {
    id: string;
    name: string;
    is_dynamic?: boolean | null;
  } | null;
}

type WizardStep =
  | "upload"
  | "mapping"
  | "preview"
  | "replacePreview"
  | "importing"
  | "results";

export default function SegmentImportWizard({
  isOpen,
  onClose,
  onSuccess,
  mode = "create",
  targetSegment = null,
}: SegmentImportWizardProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAppendMode = mode === "append";

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedCsvResult | null>(null);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [segmentName, setSegmentName] = useState("");
  const [segmentDescription, setSegmentDescription] = useState("");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Replace-mode (roster sync) pipeline state — DESIGN_SEGMENT_REPLACE_IMPORT rev 12.
  const [syncMode, setSyncMode] = useState<"append" | "replace">("append");
  const [classification, setClassification] = useState<
    "prospect" | "unclassified"
  >("unclassified");
  const [replaceRunId, setReplaceRunId] = useState<string | null>(null);
  const [replacePreviewData, setReplacePreviewData] =
    useState<ReplacePreviewResult | null>(null);
  const [ackInput, setAckInput] = useState("");
  const [replaceResult, setReplaceResult] =
    useState<ReplaceCommitResult | null>(null);
  const [replaceBanner, setReplaceBanner] = useState<string | null>(null);
  // Code-audit H2: ONE begin_key per logical upload (parsed file) — retries
  // re-use it so the server adopts the same run instead of self-contending.
  const [replaceBeginKey, setReplaceBeginKey] = useState<string | null>(null);
  // Code-audit M5: the server can demand an ack the stored preview didn't
  // predict (membership shrank live) — force the typed-ack input on.
  const [forcedAck, setForcedAck] = useState(false);
  const [stageProgress, setStageProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  // Reset wizard state
  const resetWizard = useCallback(() => {
    setCurrentStep("upload");
    setFile(null);
    setParsedData(null);
    setMappings([]);
    setPreviewRows([]);
    setSegmentName("");
    setSegmentDescription("");
    setUpdateExisting(true);
    setImportResult(null);
    setIsProcessing(false);
    setSyncMode("append");
    setClassification("unclassified");
    setReplaceRunId(null);
    setReplacePreviewData(null);
    setAckInput("");
    setReplaceResult(null);
    setReplaceBanner(null);
    setStageProgress(null);
    setReplaceBeginKey(null);
    setForcedAck(false);
  }, []);

  // Handle file selection
  const handleFileSelect = async (selectedFile: File) => {
    if (!selectedFile.name.endsWith(".csv")) {
      showToast("error", "Please select a CSV file");
      return;
    }

    setIsProcessing(true);
    try {
      // Round-2 M: an open (uncommitted) run from the previous file must be
      // ABORTED before its identity is forgotten — otherwise it holds the
      // one-open-run slot for 24h and every new begin self-contends.
      if (replaceRunId && !replaceResult) {
        try {
          await abortImportRun(replaceRunId);
        } catch (e) {
          showToast(
            "error",
            `Could not cancel the previous upload's import run — resolve that before starting a new file (${
              e instanceof Error ? e.message : "unknown error"
            })`,
          );
          return; // keep the old identity; do NOT load the new file over it
        }
        // Round-3 L: clear identity IMMEDIATELY on successful abort. If CSV
        // parsing below then fails, the already-aborted run must not linger in
        // state — retrying its abort would return SEG_IMPORT_STATE forever.
        setReplaceRunId(null);
        setReplaceBeginKey(null);
        setReplacePreviewData(null);
        setForcedAck(false);
      }
      const parsed = await parseCsvFile(selectedFile);
      setFile(selectedFile);
      setParsedData(parsed);
      // New file = new logical upload: fresh idempotency key, no stale run.
      setReplaceBeginKey(null);
      setReplaceRunId(null);
      setReplacePreviewData(null);
      setForcedAck(false);

      // Auto-detect field mappings
      const detectedMappings = autoDetectMappings(parsed.headers);
      setMappings(detectedMappings);

      // Generate default segment name from filename
      const defaultName = selectedFile.name
        .replace(/\.csv$/i, "")
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      setSegmentName(isAppendMode ? (targetSegment?.name ?? "") : defaultName);

      setCurrentStep("mapping");
    } catch (error) {
      console.error("Error parsing CSV:", error);
      showToast(
        "error",
        error instanceof Error ? error.message : "Failed to parse CSV file",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  // Update a single field mapping
  const updateMapping = (
    csvColumn: string,
    contactField: ContactFieldKey | null,
  ) => {
    setMappings((prev) =>
      prev.map((m) => (m.csvColumn === csvColumn ? { ...m, contactField } : m)),
    );
  };

  // Validate mappings and move to preview
  const handleProceedToPreview = () => {
    const emailMapped = mappings.some((m) => m.contactField === "email");
    if (!emailMapped) {
      showToast("error", "Email field must be mapped to continue");
      return;
    }

    if (!isAppendMode && !segmentName.trim()) {
      showToast("error", "Please enter a segment name");
      return;
    }
    if (isAppendMode && (!targetSegment || targetSegment.is_dynamic === true)) {
      showToast("error", "Choose a static segment before appending a file");
      return;
    }

    // Generate preview
    if (parsedData) {
      const preview = previewImport(parsedData.rows, mappings, 10);
      setPreviewRows(preview);
      setCurrentStep("preview");
    }
  };

  // Execute the import
  // Replace pipeline: begin → stage all chunks → server preview → replacePreview step.
  const handleReplaceStage = async () => {
    if (!parsedData || !targetSegment) {
      return;
    }
    if (parsedData.rows.length > REPLACE_IMPORT_MAX_ROWS) {
      showToast(
        "error",
        `This file has ${parsedData.rows.length.toLocaleString()} rows — sync imports support up to ${REPLACE_IMPORT_MAX_ROWS.toLocaleString()}. Split the file or contact engineering to raise the limit.`,
      );
      return;
    }
    setCurrentStep("importing");
    setIsProcessing(true);
    setReplaceBanner(null);
    try {
      // ONE key per logical upload (code-audit H2): a transient begin/staging
      // failure retries with the SAME key → the server adopts the same run
      // (chunks are hash-idempotent), instead of orphaning it and
      // self-contending on the one-open-run guard.
      let key = replaceBeginKey;
      if (!key) {
        key = crypto.randomUUID();
        setReplaceBeginKey(key);
      }
      const begin = await beginImportRun({
        listId: targetSegment.id,
        mode: "replace",
        classification,
        filename: file?.name ?? null,
        beginKey: key,
      });
      if (
        begin.status === "aborted" ||
        begin.status === "expired" ||
        begin.status === "committed"
      ) {
        // Terminal earlier run under this key: start a genuinely new upload.
        setReplaceBeginKey(null);
        throw new Error(
          `A previous attempt of this upload is ${begin.status}. Click again to start fresh.`,
        );
      }
      setReplaceRunId(begin.run_id);
      const mapped = mapRowsForImport(parsedData.rows, mappings);
      await stageAllChunks(begin.run_id, mapped, (done, total) =>
        setStageProgress({ done, total }),
      );
      const pv = await previewImportRun(begin.run_id);
      setReplacePreviewData(pv);
      setAckInput("");
      setCurrentStep("replacePreview");
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Import failed";
      showToast("error", msg);
      setCurrentStep("preview");
    } finally {
      setIsProcessing(false);
      setStageProgress(null);
    }
  };

  const handleReplaceCommit = async () => {
    if (!replaceRunId || !replacePreviewData || !targetSegment) {
      return;
    }
    // Round-3 M: refuse an over-budget sync HERE — before prepareAllImportContacts
    // permanently creates contacts that a commit-time refusal would strand.
    // The server's identical check remains authoritative.
    if (
      exceedsCommitBudget(replacePreviewData.staged, replacePreviewData.remove)
    ) {
      setReplaceBanner(
        `This sync is too large to apply in one step (${replacePreviewData.staged.toLocaleString()} rows in the file, ${replacePreviewData.remove.toLocaleString()} members to remove). Split the file, or reduce how many current members the file omits, then re-upload.`,
      );
      return;
    }
    const needsAck =
      forcedAck ||
      isLargeRemoval(
        replacePreviewData.remove,
        replacePreviewData.current_members,
      );
    if (needsAck && ackInput.trim() !== (targetSegment.name ?? "").trim()) {
      setReplaceBanner(
        `Type the segment name exactly ("${targetSegment.name}") to confirm removing ${replacePreviewData.remove.toLocaleString()} members.`,
      );
      return;
    }
    setIsProcessing(true);
    setReplaceBanner(null);
    setCurrentStep("importing");
    try {
      // Bounded contact-creation phase (8s-budget fix): idempotent, resumable.
      if (replacePreviewData.new_contacts > 0) {
        setStageProgress({
          done: 0,
          total: replacePreviewData.new_contacts,
        });
        await prepareAllImportContacts(replaceRunId, (remaining) =>
          setStageProgress({
            done: replacePreviewData.new_contacts - remaining,
            total: replacePreviewData.new_contacts,
          }),
        );
        setStageProgress(null);
      }
      const result = await commitImportRun({
        runId: replaceRunId,
        expectedRemoveCount: replacePreviewData.remove,
        expectedRemoveHash: replacePreviewData.remove_set_hash,
        acknowledgeLargeRemoval: needsAck,
      });
      setReplaceResult(result);
      setCurrentStep("results");
      showToast(
        "success",
        `Synced ${targetSegment.name}: +${result.added_members} added, -${result.actual_removed} removed`,
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Commit failed";
      if (classifyImportError(msg) === "ack_required") {
        // Live membership shrank below the rail threshold since preview
        // (code-audit M5): recompute the preview and FORCE the typed-ack
        // input regardless of what the client-side rail mirror says.
        try {
          const pv = await previewImportRun(replaceRunId);
          setReplacePreviewData(pv);
          setForcedAck(true);
          setAckInput("");
          setReplaceBanner(
            "The segment shrank while you were reviewing, so this sync now needs explicit confirmation. Review the updated numbers and type the segment name.",
          );
        } catch {
          setForcedAck(true);
          setReplaceBanner(
            "This sync now requires explicit confirmation. Type the segment name and try again.",
          );
        }
        setCurrentStep("replacePreview");
      } else if (classifyImportError(msg) === "stale_preview") {
        // Membership changed since preview — recompute and re-show (rev 12 §5.4).
        try {
          const pv = await previewImportRun(replaceRunId);
          setReplacePreviewData(pv);
          setAckInput("");
          setReplaceBanner(
            "The segment changed while you were reviewing — here are the updated numbers. Confirm again to proceed.",
          );
          setCurrentStep("replacePreview");
        } catch (e2) {
          showToast(
            "error",
            e2 instanceof Error ? e2.message : "Re-preview failed",
          );
          setCurrentStep("replacePreview");
        }
      } else {
        showToast("error", msg);
        setCurrentStep("replacePreview");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!parsedData || !user) {
      return;
    }

    if (isAppendMode && syncMode === "replace") {
      await handleReplaceStage();
      return;
    }

    setCurrentStep("importing");
    setIsProcessing(true);

    try {
      const result =
        isAppendMode && targetSegment
          ? await appendToSegment(
              parsedData.rows,
              mappings,
              targetSegment.id,
              user.id,
              { updateExisting },
            )
          : await executeImport(
              parsedData.rows,
              mappings,
              segmentName.trim(),
              segmentDescription.trim() || undefined,
              user.id,
              { updateExisting },
            );

      setImportResult(result);
      setCurrentStep("results");

      if (result.success && result.segmentId) {
        showToast(
          "success",
          isAppendMode
            ? `Added ${result.importedCount.toLocaleString()} contact${result.importedCount === 1 ? "" : "s"} to ${targetSegment?.name ?? "segment"}`
            : `Imported ${result.importedCount} contact${result.importedCount === 1 ? "" : "s"} to segment`,
        );
      } else if (result.batchFailedCount > 0) {
        showToast(
          "error",
          `Partial import: ${result.batchFailedCount} row(s) failed. The segment is incomplete — re-import before using it.`,
        );
      }
    } catch (error) {
      console.error("Import error:", error);
      showToast(
        "error",
        error instanceof Error ? error.message : "Import failed",
      );
      setCurrentStep("preview");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (isProcessing) {
      if (!confirm("Import in progress. Are you sure you want to cancel?")) {
        return;
      }
    }
    // Fire-and-forget abort of an open (uncommitted) sync run so the segment
    // isn't left locked behind the one-open-run constraint; the 24h server
    // sweep is the backstop if this request is lost.
    if (replaceRunId && !replaceResult) {
      abortImportRun(replaceRunId).catch(() => {});
    }
    resetWizard();
    onClose();
  };

  // Handle success completion
  const handleComplete = () => {
    if (importResult?.segmentId) {
      onSuccess(importResult.segmentId);
    } else if (replaceResult && targetSegment) {
      onSuccess(targetSegment.id);
    }
    resetWizard();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isAppendMode
                  ? "Append Contacts to Segment"
                  : "Import Contacts to Segment"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {currentStep === "upload" &&
                  (isAppendMode
                    ? `Upload a CSV file to append to ${targetSegment?.name ?? "this segment"}`
                    : "Upload a CSV file with contact data")}
                {currentStep === "mapping" &&
                  "Map CSV columns to contact fields"}
                {currentStep === "preview" && "Review data before importing"}
                {currentStep === "importing" && "Importing contacts..."}
                {currentStep === "results" && "Import complete"}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              {["upload", "mapping", "preview", "results"].map(
                (step, index) => {
                  const stepLabels = {
                    upload: "Upload",
                    mapping: "Map Fields",
                    preview: "Preview",
                    results: "Results",
                  };
                  const stepIndex = [
                    "upload",
                    "mapping",
                    "preview",
                    "results",
                  ].indexOf(
                    currentStep === "importing" ? "results" : currentStep,
                  );
                  const isActive =
                    step === currentStep ||
                    (currentStep === "importing" && step === "results");
                  const isComplete = index < stepIndex;

                  return (
                    <React.Fragment key={step}>
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            isComplete
                              ? "bg-green-500 text-white"
                              : isActive
                                ? "bg-primary-600 text-white"
                                : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          {isComplete ? (
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </div>
                        <span
                          className={`ml-2 text-sm font-medium ${isActive ? "text-primary-600" : "text-gray-500"}`}
                        >
                          {stepLabels[step as keyof typeof stepLabels]}
                        </span>
                      </div>
                      {index < 3 && (
                        <div
                          className={`flex-1 h-0.5 mx-4 ${index < stepIndex ? "bg-green-500" : "bg-gray-200"}`}
                        />
                      )}
                    </React.Fragment>
                  );
                },
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
            {/* Upload Step */}
            {currentStep === "upload" && (
              <label
                className={`relative block border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                  dragActive
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) =>
                    e.target.files?.[0] && handleFileSelect(e.target.files[0])
                  }
                  className="sr-only"
                />
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mt-4 text-lg font-medium text-gray-900">
                  Drop your CSV file here
                </p>
                <p className="mt-2 text-sm text-gray-500">or click to browse</p>
                <span className="mt-4 inline-flex px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Select CSV File
                </span>
                <p className="mt-4 text-xs text-gray-400">
                  CSV file must include at least an email column
                </p>
              </label>
            )}

            {/* Mapping Step */}
            {currentStep === "mapping" && parsedData && (
              <div className="space-y-6">
                {/* Segment Details */}
                {isAppendMode ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="text-sm font-medium text-blue-900">
                      Target segment
                    </div>
                    <div className="mt-1 text-sm text-blue-800">
                      {targetSegment?.name ?? "Static segment"}
                    </div>
                    <p className="mt-2 text-xs text-blue-700">
                      Contacts that are already in this segment will be skipped.
                      New email addresses will be created as contacts and then
                      added to this segment.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Segment Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={segmentName}
                        onChange={(e) => setSegmentName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter segment name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description{" "}
                        <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={segmentDescription}
                        onChange={(e) => setSegmentDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter description"
                      />
                    </div>
                  </div>
                )}

                {/* File Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg
                        className="w-8 h-8 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {file?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {parsedData.totalRows.toLocaleString()} rows,{" "}
                          {parsedData.headers.length} columns
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setFile(null);
                        setParsedData(null);
                        setCurrentStep("upload");
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Change file
                    </button>
                  </div>
                </div>

                {/* Field Mappings */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Map CSV Columns to Contact Fields
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            CSV Column
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Sample Data
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Map to Field
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {mappings.map((mapping) => {
                          const sampleData = parsedData.rows
                            .slice(0, 3)
                            .map((row) => row[mapping.csvColumn])
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <tr key={mapping.csvColumn}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {mapping.csvColumn}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                                {sampleData || (
                                  <span className="text-gray-300 italic">
                                    empty
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={mapping.contactField || ""}
                                  onChange={(e) =>
                                    updateMapping(
                                      mapping.csvColumn,
                                      (e.target.value ||
                                        null) as ContactFieldKey | null,
                                    )
                                  }
                                  className={`w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                    mapping.contactField === "email"
                                      ? "border-green-500 bg-green-50"
                                      : mapping.contactField
                                        ? "border-blue-300 bg-blue-50"
                                        : "border-gray-300"
                                  }`}
                                >
                                  <option value="">-- Do not import --</option>
                                  {CONTACT_FIELD_OPTIONS.map((field) => (
                                    <option
                                      key={field.value}
                                      value={field.value}
                                      disabled={mappings.some(
                                        (m) =>
                                          m.contactField === field.value &&
                                          m.csvColumn !== mapping.csvColumn,
                                      )}
                                    >
                                      {field.label}
                                      {field.required ? " *" : ""}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Options */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="updateExisting"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="updateExisting"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Update existing contacts if email already exists
                  </label>
                </div>
              </div>
            )}

            {/* Preview Step */}
            {currentStep === "preview" && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {parsedData?.totalRows.toLocaleString()}
                    </div>
                    <div className="text-sm text-blue-600">Total Rows</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {previewRows.filter((r) => r.isValid).length}
                    </div>
                    <div className="text-sm text-green-600">
                      Valid in Preview
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {previewRows.filter((r) => !r.isValid).length}
                    </div>
                    <div className="text-sm text-red-600">
                      Errors in Preview
                    </div>
                  </div>
                </div>

                {/* Preview Table */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Preview (first 10 rows)
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Row
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          {mappings
                            .filter((m) => m.contactField)
                            .map((m) => (
                              <th
                                key={m.csvColumn}
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                {
                                  CONTACT_FIELD_OPTIONS.find(
                                    (f) => f.value === m.contactField,
                                  )?.label
                                }
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewRows.map((row) => (
                          <tr
                            key={row.rowNumber}
                            className={row.isValid ? "" : "bg-red-50"}
                          >
                            <td className="px-3 py-2 text-sm text-gray-500">
                              {row.rowNumber}
                            </td>
                            <td className="px-3 py-2">
                              {row.isValid ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Valid
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                                  title={row.errors.join("; ")}
                                >
                                  Error
                                </span>
                              )}
                            </td>
                            {mappings
                              .filter((m) => m.contactField)
                              .map((m) => (
                                <td
                                  key={m.csvColumn}
                                  className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate"
                                >
                                  {row.data[m.csvColumn] || (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Errors */}
                {previewRows.some((r) => !r.isValid) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Validation Errors
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {previewRows
                        .filter((r) => !r.isValid)
                        .flatMap((r) =>
                          r.errors.map((e, i) => (
                            <li key={`${r.rowNumber}-${i}`}>
                              Row {r.rowNumber}: {e}
                            </li>
                          )),
                        )}
                    </ul>
                  </div>
                )}

                {/* Import Settings Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {isAppendMode ? "Append Settings" : "Import Settings"}
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-gray-500">
                      {isAppendMode ? "Target Segment:" : "Segment Name:"}
                    </dt>
                    <dd className="text-gray-900 font-medium">{segmentName}</dd>
                    <dt className="text-gray-500">Update Existing:</dt>
                    <dd className="text-gray-900">
                      {updateExisting ? "Yes" : "No"}
                    </dd>
                    <dt className="text-gray-500">Mapped Fields:</dt>
                    <dd className="text-gray-900">
                      {mappings.filter((m) => m.contactField).length} of{" "}
                      {mappings.length}
                    </dd>
                  </dl>
                </div>

                {/* Sync mode (append target only): append vs replace pipeline */}
                {isAppendMode && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      How should this file be applied?
                    </h4>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="syncMode"
                        checked={syncMode === "append"}
                        onChange={() => setSyncMode("append")}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-gray-900">
                          Add to segment (append)
                        </span>
                        <span className="block text-gray-500">
                          Everyone in the file is added; current members stay.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="syncMode"
                        checked={syncMode === "replace"}
                        onChange={() => setSyncMode("replace")}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-gray-900">
                          Sync segment to this file (replace)
                        </span>
                        <span className="block text-gray-500">
                          The file becomes the roster: new people are added and{" "}
                          <span className="font-medium text-red-700">
                            current members missing from the file are removed
                          </span>
                          . You will review exact add/remove counts before
                          anything changes.
                        </span>
                      </span>
                    </label>
                    {syncMode === "replace" && (
                      <div className="pl-7">
                        <label className="block text-xs text-gray-600 mb-1">
                          New contacts found in the file import as
                        </label>
                        <select
                          value={classification}
                          onChange={(e) =>
                            setClassification(
                              e.target.value as "prospect" | "unclassified",
                            )
                          }
                          className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="unclassified">
                            Unclassified (recommended for partner rosters)
                          </option>
                          <option value="prospect">Prospect</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Replace preview step: server-computed roster diff + typed ack */}
            {currentStep === "replacePreview" && replacePreviewData && (
              <div className="space-y-4">
                {replaceBanner && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    {replaceBanner}
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Sync preview — nothing has changed yet
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-gray-500">Rows staged from file:</dt>
                    <dd className="text-gray-900">
                      {replacePreviewData.staged.toLocaleString()}
                      {replacePreviewData.invalid_total > 0 &&
                        ` (${replacePreviewData.invalid_total} invalid rows skipped)`}
                    </dd>
                    <dt className="text-gray-500">Will be added:</dt>
                    <dd className="text-green-700 font-medium">
                      {replacePreviewData.add.toLocaleString()}
                      {replacePreviewData.new_contacts > 0 &&
                        ` (${replacePreviewData.new_contacts} brand-new contacts)`}
                    </dd>
                    <dt className="text-gray-500">Will stay:</dt>
                    <dd className="text-gray-900">
                      {replacePreviewData.keep.toLocaleString()}
                    </dd>
                    <dt className="text-gray-500">Will be REMOVED:</dt>
                    <dd className="text-red-700 font-semibold">
                      {replacePreviewData.remove.toLocaleString()}
                      {replacePreviewData.remove_no_email > 0 &&
                        ` (${replacePreviewData.remove_no_email} have no email on file)`}
                    </dd>
                  </dl>
                </div>
                {replacePreviewData.remove > 0 && (
                  <div className="bg-white border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Sample of members being removed
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-0.5 max-h-40 overflow-y-auto">
                      {replacePreviewData.remove_sample.map((s, i) => (
                        <li key={i}>
                          {s.email ?? "(no email)"}
                          {s.name ? ` — ${s.name}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(forcedAck ||
                  isLargeRemoval(
                    replacePreviewData.remove,
                    replacePreviewData.current_members,
                  )) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 mb-2">
                      This sync removes{" "}
                      <strong>
                        {replacePreviewData.remove.toLocaleString()} of{" "}
                        {replacePreviewData.current_members.toLocaleString()}
                      </strong>{" "}
                      current members. Type the segment name to confirm:
                    </p>
                    <input
                      type="text"
                      value={ackInput}
                      onChange={(e) => setAckInput(e.target.value)}
                      placeholder={targetSegment?.name ?? ""}
                      className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Importing Step */}
            {currentStep === "importing" && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-lg font-medium text-gray-900">
                  {stageProgress
                    ? "Uploading file to server..."
                    : "Importing contacts..."}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {stageProgress
                    ? `${stageProgress.done.toLocaleString()} of ${stageProgress.total.toLocaleString()} rows staged`
                    : "This may take a few moments for large files"}
                </p>
              </div>
            )}

            {/* Replace (sync) Results */}
            {currentStep === "results" && replaceResult && (
              <div className="space-y-6">
                <div className="rounded-lg p-6 bg-green-50 border border-green-200">
                  <p className="text-lg font-medium text-green-900">
                    Segment synced to file
                  </p>
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-gray-600">Added:</dt>
                    <dd className="text-gray-900 font-medium">
                      {replaceResult.added_members.toLocaleString()}
                      {replaceResult.new_contacts > 0 &&
                        ` (${replaceResult.new_contacts} new contacts created)`}
                    </dd>
                    <dt className="text-gray-600">Removed:</dt>
                    <dd className="text-gray-900 font-medium">
                      {replaceResult.actual_removed.toLocaleString()}
                    </dd>
                    <dt className="text-gray-600">Members before → after:</dt>
                    <dd className="text-gray-900 font-medium">
                      {replaceResult.members_before.toLocaleString()} →{" "}
                      {replaceResult.members_after.toLocaleString()}
                    </dd>
                  </dl>
                  <p className="mt-3 text-xs text-gray-500">
                    Removed members are recorded in the import audit and can be
                    re-added if this was a mistake.
                  </p>
                </div>
              </div>
            )}

            {/* Results Step */}
            {currentStep === "results" && importResult && (
              <div className="space-y-6">
                {/* Success/Error Banner */}
                <div
                  className={`rounded-lg p-6 ${
                    importResult.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <div className="flex items-center">
                    {importResult.success ? (
                      <svg
                        className="h-8 w-8 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-8 w-8 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                    <div className="ml-4">
                      <h3
                        className={`text-lg font-medium ${
                          importResult.success
                            ? "text-green-800"
                            : "text-red-800"
                        }`}
                      >
                        {importResult.success
                          ? "Import Completed Successfully"
                          : "Import Completed with Errors"}
                      </h3>
                      <p
                        className={`text-sm ${
                          importResult.success
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {importResult.success
                          ? isAppendMode
                            ? `Segment "${segmentName}" has been updated`
                            : `Segment "${segmentName}" has been created`
                          : isAppendMode
                            ? `Segment "${segmentName}" was partially updated — ${importResult.batchFailedCount.toLocaleString()} row(s) did not import (see errors below). Re-run the append before using this segment for sending.`
                            : `Segment "${segmentName}" was created but the import is INCOMPLETE — ${importResult.batchFailedCount.toLocaleString()} row(s) did not import (see errors below). Do not use this segment for sending until you re-run the import to add the remaining contacts.`}
                      </p>
                    </div>
                  </div>
                </div>

                {/*
                  Stats. 2026-05-27 (Item H): rewrote labels + buckets:
                  - "New Contacts" → "Added to Segment" (the RPC's
                    `added` is count of list_contacts rows added, not
                    new contacts; both new AND pre-existing contacts
                    flow through).
                  - "Updated" hidden — the RPC never increments it and
                    an always-zero stat was misleading. True
                    created-vs-existing accounting is a separate RPC
                    enhancement.
                  - "Skipped" renamed to "Invalid Rows" (client-side
                    validation rejections only).
                  - New "Duplicates" amber cell for upload-internal
                    dedup.
                  - New "Batch Failed" red cell rendered only when > 0;
                    should be zero in normal cases after the server-side
                    dedup migration.
                */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {importResult.totalRows.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">Total Rows</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importResult.importedCount.toLocaleString()}
                    </div>
                    <div
                      className="text-sm text-green-600"
                      title="Rows added to this segment. Includes both newly-created contacts and pre-existing contacts that the upload added to the segment."
                    >
                      Added to Segment
                    </div>
                  </div>
                  {isAppendMode && (
                    <div
                      className="bg-blue-50 rounded-lg p-4 text-center"
                      title="Valid uploaded contacts that were already in the target segment."
                    >
                      <div className="text-2xl font-bold text-blue-600">
                        {(
                          importResult.alreadyMemberCount ?? 0
                        ).toLocaleString()}
                      </div>
                      <div className="text-sm text-blue-600">
                        Already in Segment
                      </div>
                    </div>
                  )}
                  {typeof importResult.newContactCount === "number" && (
                    <div
                      className="bg-indigo-50 rounded-lg p-4 text-center"
                      title="Contacts created in the CRM from new email addresses in this upload."
                    >
                      <div className="text-2xl font-bold text-indigo-600">
                        {importResult.newContactCount.toLocaleString()}
                      </div>
                      <div className="text-sm text-indigo-600">
                        New CRM Contacts
                      </div>
                    </div>
                  )}
                  <div
                    className="bg-amber-50 rounded-lg p-4 text-center"
                    title="Rows whose email already appeared earlier in this upload file. Each email is imported once — only the first occurrence."
                  >
                    <div className="text-2xl font-bold text-amber-600">
                      {importResult.duplicateCount.toLocaleString()}
                    </div>
                    <div className="text-sm text-amber-600">
                      Duplicates in Upload
                    </div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {importResult.skippedCount.toLocaleString()}
                    </div>
                    <div className="text-sm text-yellow-600">Invalid Rows</div>
                  </div>
                  {importResult.batchFailedCount > 0 && (
                    <div className="bg-red-50 rounded-lg p-4 text-center md:col-span-1">
                      <div className="text-2xl font-bold text-red-600">
                        {importResult.batchFailedCount.toLocaleString()}
                      </div>
                      <div className="text-sm text-red-600">Batch Failed</div>
                    </div>
                  )}
                </div>

                {/*
                  Warnings (amber). 2026-05-27 (Item H): separate panel
                  from red errors. Used for upload-internal duplicate
                  notices so a successful dedup doesn't look like a
                  partial failure.
                */}
                {importResult.warnings.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-amber-800 mb-2">
                      Duplicates in Upload ({importResult.duplicateCount} total,
                      showing first {importResult.warnings.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                      <ul className="text-sm text-amber-700 space-y-1">
                        {importResult.warnings.map((w, index) => (
                          <li key={index}>
                            Row {w.row}: {w.warning}
                          </li>
                        ))}
                        {importResult.duplicateCount >
                          importResult.warnings.length && (
                          <li className="text-amber-500 italic">
                            ...and{" "}
                            {importResult.duplicateCount -
                              importResult.warnings.length}{" "}
                            more duplicates not shown
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Errors (red) */}
                {importResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Errors ({importResult.errors.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                      <ul className="text-sm text-red-700 space-y-1">
                        {importResult.errors
                          .slice(0, 20)
                          .map((error, index) => (
                            <li key={index}>
                              {error.row > 0 ? `Row ${error.row}: ` : ""}
                              {error.error}
                            </li>
                          ))}
                        {importResult.errors.length > 20 && (
                          <li className="text-red-500 italic">
                            ...and {importResult.errors.length - 20} more errors
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {currentStep === "results" ? "Close" : "Cancel"}
            </button>

            <div className="flex items-center gap-3">
              {currentStep === "mapping" && (
                <button
                  onClick={() => setCurrentStep("upload")}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Back
                </button>
              )}
              {currentStep === "preview" && (
                <button
                  onClick={() => setCurrentStep("mapping")}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Back
                </button>
              )}

              {currentStep === "mapping" && (
                <button
                  onClick={handleProceedToPreview}
                  className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                >
                  Preview Import
                </button>
              )}

              {currentStep === "preview" && (
                <button
                  onClick={handleExecuteImport}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {isAppendMode
                    ? syncMode === "replace"
                      ? "Upload & Preview Sync"
                      : "Append Contacts"
                    : "Start Import"}
                </button>
              )}

              {currentStep === "replacePreview" && (
                <>
                  <button
                    onClick={async () => {
                      if (replaceRunId) {
                        try {
                          await abortImportRun(replaceRunId);
                        } catch {
                          /* run may already be expired; close regardless */
                        }
                      }
                      setReplaceRunId(null);
                      setReplacePreviewData(null);
                      setCurrentStep("preview");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel Sync
                  </button>
                  <button
                    onClick={handleReplaceCommit}
                    disabled={isProcessing}
                    className="px-6 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {replacePreviewData && replacePreviewData.remove > 0
                      ? `Sync (remove ${replacePreviewData.remove.toLocaleString()})`
                      : "Sync Segment"}
                  </button>
                </>
              )}

              {currentStep === "results" &&
                (importResult?.success || replaceResult) && (
                  <button
                    onClick={handleComplete}
                    className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                  >
                    {isAppendMode ? "Done" : "View Segment"}
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
